import { z } from "zod";

import { AtomKindSchema, AtomStatusSchema } from "../model/atom.js";
import { CellEffectSchema } from "../model/cell.js";
import {
  AtomIdSchema,
  BoardProfileSchema,
  CellIdSchema,
  PositionRefSchema,
  SessionKeySchema,
} from "../model/common.js";
import { CliError } from "../errors/index.js";

type OptionValue = boolean | string | string[];
type RawCliArgs = {
  options: Record<string, OptionValue>;
  positionals: string[];
};

const StringListSchema = z
  .union([z.string(), z.array(z.string())])
  .transform((value) =>
    (Array.isArray(value) ? value : [value])
      .flatMap((item) => item.split(","))
      .map((item) => item.trim())
      .filter(Boolean),
  );

const DepthSchema = z.coerce.number().int().positive().default(4);
const CompactModeSchema = z.enum(["state"]).default("state");
const ProofVerdictSchema = z.enum(["fail", "pass"]);

function optionalString() {
  return z.preprocess((value) => {
    if (value === null || value === undefined || value === "") {
      return undefined;
    }
    return value;
  }, z.string().optional());
}

export const ContextShowRequestSchema = z.object({
  cmd: z.literal("context.show"),
});
export type ContextShowRequest = z.infer<typeof ContextShowRequestSchema>;

export const BoardCreateRequestSchema = z.object({
  cmd: z.literal("board.create"),
  p: BoardProfileSchema.default("universal"),
  s: SessionKeySchema,
});
export type BoardCreateRequest = z.infer<typeof BoardCreateRequestSchema>;

export const BoardShowRequestSchema = z.object({
  cmd: z.literal("board.show"),
  s: SessionKeySchema,
});
export type BoardShowRequest = z.infer<typeof BoardShowRequestSchema>;

export const BoardJudgeRequestSchema = z.object({
  cmd: z.literal("board.judge"),
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
});
export type BoardJudgeRequest = z.infer<typeof BoardJudgeRequestSchema>;

export const BoardListRequestSchema = z.object({
  cmd: z.literal("board.list"),
});
export type BoardListRequest = z.infer<typeof BoardListRequestSchema>;

export const AtomUpsertRequestSchema = z.object({
  a: optionalString().pipe(AtomIdSchema.optional()),
  b: z.string().optional(),
  cmd: z.literal("atom.upsert"),
  g: StringListSchema.default([]),
  k: AtomKindSchema,
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
  st: AtomStatusSchema.optional(),
  t: z.string().optional(),
});
export type AtomUpsertRequest = z.infer<typeof AtomUpsertRequestSchema>;

export const AtomListRequestSchema = z.object({
  cmd: z.literal("atom.list"),
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
});
export type AtomListRequest = z.infer<typeof AtomListRequestSchema>;

export const AtomShowRequestSchema = z.object({
  a: AtomIdSchema,
  cmd: z.literal("atom.show"),
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
});
export type AtomShowRequest = z.infer<typeof AtomShowRequestSchema>;

export const CellUpsertRequestSchema = z.object({
  c: optionalString().pipe(CellIdSchema.optional()),
  cmd: z.literal("cell.upsert"),
  e: CellEffectSchema,
  f: AtomIdSchema,
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
  t: AtomIdSchema,
});
export type CellUpsertRequest = z.infer<typeof CellUpsertRequestSchema>;

export const CellListRequestSchema = z.object({
  cmd: z.literal("cell.list"),
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
});
export type CellListRequest = z.infer<typeof CellListRequestSchema>;

export const LineSelectRequestSchema = z.object({
  a: AtomIdSchema,
  cmd: z.literal("line.select"),
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
});
export type LineSelectRequest = z.infer<typeof LineSelectRequestSchema>;

export const LineShowRequestSchema = z.object({
  cmd: z.literal("line.show"),
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
});
export type LineShowRequest = z.infer<typeof LineShowRequestSchema>;

export const ProofRecordRequestSchema = z.object({
  b: z.string().optional(),
  cmd: z.literal("proof.record"),
  f: optionalString().pipe(AtomIdSchema.optional()),
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
  t: AtomIdSchema,
  v: ProofVerdictSchema,
});
export type ProofRecordRequest = z.infer<typeof ProofRecordRequestSchema>;

export const ProofListRequestSchema = z.object({
  cmd: z.literal("proof.list"),
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
});
export type ProofListRequest = z.infer<typeof ProofListRequestSchema>;

export const SearchNextRequestSchema = z.object({
  cmd: z.literal("search.next"),
  d: DepthSchema,
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
});
export type SearchNextRequest = z.infer<typeof SearchNextRequestSchema>;

export const SearchShowRequestSchema = z.object({
  cmd: z.literal("search.show"),
  d: DepthSchema,
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
});
export type SearchShowRequest = z.infer<typeof SearchShowRequestSchema>;

export const CompactRequestSchema = z.object({
  cmd: z.literal("compact"),
  m: CompactModeSchema,
  p: PositionRefSchema.optional(),
  s: SessionKeySchema,
});
export type CompactRequest = z.infer<typeof CompactRequestSchema>;

export const DictGetRequestSchema = z.object({
  cmd: z.literal("dict.get"),
  k: z.string().min(1),
  s: SessionKeySchema,
});
export type DictGetRequest = z.infer<typeof DictGetRequestSchema>;

export const DictGetAllRequestSchema = z.object({
  cmd: z.literal("dict.getAll"),
  s: SessionKeySchema,
});
export type DictGetAllRequest = z.infer<typeof DictGetAllRequestSchema>;

export const DictSetRequestSchema = z.object({
  cmd: z.literal("dict.set"),
  k: z.string().min(1),
  s: SessionKeySchema,
  v: z.string(),
});
export type DictSetRequest = z.infer<typeof DictSetRequestSchema>;

export const DictUnsetRequestSchema = z.object({
  cmd: z.literal("dict.unset"),
  k: z.string().min(1),
  s: SessionKeySchema,
});
export type DictUnsetRequest = z.infer<typeof DictUnsetRequestSchema>;

export const CommandRequestSchema = z.discriminatedUnion("cmd", [
  ContextShowRequestSchema,
  BoardCreateRequestSchema,
  BoardShowRequestSchema,
  BoardJudgeRequestSchema,
  BoardListRequestSchema,
  AtomUpsertRequestSchema,
  AtomListRequestSchema,
  AtomShowRequestSchema,
  CellUpsertRequestSchema,
  CellListRequestSchema,
  LineSelectRequestSchema,
  LineShowRequestSchema,
  ProofRecordRequestSchema,
  ProofListRequestSchema,
  SearchNextRequestSchema,
  SearchShowRequestSchema,
  CompactRequestSchema,
  DictGetRequestSchema,
  DictGetAllRequestSchema,
  DictSetRequestSchema,
  DictUnsetRequestSchema,
]);
export type CommandRequest = z.infer<typeof CommandRequestSchema>;
export type RequestSequence = readonly CommandRequest[];
export type CliRequest = CommandRequest | RequestSequence;

type ParsedCliArgs = {
  cwd: string | undefined;
  help: boolean;
  json: boolean;
  pretty: boolean;
  request: CliRequest | undefined;
  version: boolean;
};

type CommandDefinition = {
  aliases: Record<string, string>;
  arrayOrder: readonly string[];
  description: string;
  fromArray: (values: readonly unknown[]) => unknown;
  fromFlags: (options: Record<string, OptionValue>, operands: string[]) => unknown;
  path: readonly string[];
  schema: {
    parse: (value: unknown) => CommandRequest;
  };
};

const COMMAND_DEFINITIONS = [
  defineCommand({
    aliases: {},
    arrayOrder: [],
    description: "Resolve project context from cwd",
    fromArray: () => ({ cmd: "context.show" }),
    fromFlags: () => ({ cmd: "context.show" }),
    path: ["context", "show"],
    schema: ContextShowRequestSchema,
  }),
  defineCommand({
    aliases: { p: "p", profile: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p"],
    description: "Create a board strictly once",
    fromArray: ([, , s, p]) => ({ cmd: "board.create", p, s }),
    fromFlags: (options) => ({ cmd: "board.create", p: readValue(options, "p"), s: readValue(options, "s") }),
    path: ["board", "create"],
    schema: BoardCreateRequestSchema,
  }),
  defineCommand({
    aliases: { s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s"],
    description: "Show board metadata and the current tip",
    fromArray: ([, , s]) => ({ cmd: "board.show", s }),
    fromFlags: (options) => ({ cmd: "board.show", s: readValue(options, "s") }),
    path: ["board", "show"],
    schema: BoardShowRequestSchema,
  }),
  defineCommand({
    aliases: { p: "p", position: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p"],
    description: "Judge the current board phase and attention debt",
    fromArray: ([, , s, p]) => ({ cmd: "board.judge", p: normalizeOptionalValue(p), s }),
    fromFlags: (options) => ({ cmd: "board.judge", p: readValue(options, "p"), s: readValue(options, "s") }),
    path: ["board", "judge"],
    schema: BoardJudgeRequestSchema,
  }),
  defineCommand({
    aliases: {},
    arrayOrder: [],
    description: "List boards under the current project",
    fromArray: () => ({ cmd: "board.list" }),
    fromFlags: () => ({ cmd: "board.list" }),
    path: ["board", "list"],
    schema: BoardListRequestSchema,
  }),
  defineCommand({
    aliases: {
      a: "a",
      atom: "a",
      b: "b",
      body: "b",
      g: "g",
      p: "p",
      position: "p",
      s: "s",
      "session-key": "s",
      sk: "s",
      st: "st",
      status: "st",
      t: "t",
      title: "t",
      type: "k",
      k: "k",
    },
    arrayOrder: ["s", "p", "a", "k", "b", "t", "g", "st"],
    description: "Create or update an atom from a position",
    fromArray: ([, , s, p, a, k, b, t, g, st]) => ({
      a: normalizeOptionalValue(a),
      b: normalizeOptionalValue(b),
      cmd: "atom.upsert",
      g: normalizeOptionalValue(g),
      k,
      p: normalizeOptionalValue(p),
      s,
      st: normalizeOptionalValue(st),
      t: normalizeOptionalValue(t),
    }),
    fromFlags: (options, operands) => ({
      a: readValue(options, "a"),
      b: readValue(options, "b") ?? operands[0],
      cmd: "atom.upsert",
      g: readValues(options, "g"),
      k: readValue(options, "k"),
      p: readValue(options, "p"),
      s: readValue(options, "s"),
      st: readValue(options, "st"),
      t: readValue(options, "t"),
    }),
    path: ["atom", "upsert"],
    schema: AtomUpsertRequestSchema,
  }),
  defineCommand({
    aliases: { p: "p", position: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p"],
    description: "List atoms from a position snapshot",
    fromArray: ([, , s, p]) => ({ cmd: "atom.list", p: normalizeOptionalValue(p), s }),
    fromFlags: (options) => ({ cmd: "atom.list", p: readValue(options, "p"), s: readValue(options, "s") }),
    path: ["atom", "list"],
    schema: AtomListRequestSchema,
  }),
  defineCommand({
    aliases: { a: "a", atom: "a", p: "p", position: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p", "a"],
    description: "Show one atom from a position snapshot",
    fromArray: ([, , s, p, a]) => ({ a, cmd: "atom.show", p: normalizeOptionalValue(p), s }),
    fromFlags: (options) => ({ a: readValue(options, "a"), cmd: "atom.show", p: readValue(options, "p"), s: readValue(options, "s") }),
    path: ["atom", "show"],
    schema: AtomShowRequestSchema,
  }),
  defineCommand({
    aliases: {
      c: "c",
      cell: "c",
      e: "e",
      effect: "e",
      f: "f",
      from: "f",
      p: "p",
      position: "p",
      s: "s",
      "session-key": "s",
      sk: "s",
      t: "t",
      to: "t",
    },
    arrayOrder: ["s", "p", "c", "f", "e", "t"],
    description: "Create or update a cell from a position",
    fromArray: ([, , s, p, c, f, e, t]) => ({
      c: normalizeOptionalValue(c),
      cmd: "cell.upsert",
      e,
      f,
      p: normalizeOptionalValue(p),
      s,
      t,
    }),
    fromFlags: (options) => ({
      c: readValue(options, "c"),
      cmd: "cell.upsert",
      e: readValue(options, "e"),
      f: readValue(options, "f"),
      p: readValue(options, "p"),
      s: readValue(options, "s"),
      t: readValue(options, "t"),
    }),
    path: ["cell", "upsert"],
    schema: CellUpsertRequestSchema,
  }),
  defineCommand({
    aliases: { p: "p", position: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p"],
    description: "List cells from a position snapshot",
    fromArray: ([, , s, p]) => ({ cmd: "cell.list", p: normalizeOptionalValue(p), s }),
    fromFlags: (options) => ({ cmd: "cell.list", p: readValue(options, "p"), s: readValue(options, "s") }),
    path: ["cell", "list"],
    schema: CellListRequestSchema,
  }),
  defineCommand({
    aliases: { a: "a", path: "a", p: "p", position: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p", "a"],
    description: "Select the current mainline path",
    fromArray: ([, , s, p, a]) => ({ a, cmd: "line.select", p: normalizeOptionalValue(p), s }),
    fromFlags: (options) => ({ a: readValue(options, "a"), cmd: "line.select", p: readValue(options, "p"), s: readValue(options, "s") }),
    path: ["line", "select"],
    schema: LineSelectRequestSchema,
  }),
  defineCommand({
    aliases: { p: "p", position: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p"],
    description: "Show the selected line and its nearby threats",
    fromArray: ([, , s, p]) => ({ cmd: "line.show", p: normalizeOptionalValue(p), s }),
    fromFlags: (options) => ({ cmd: "line.show", p: readValue(options, "p"), s: readValue(options, "s") }),
    path: ["line", "show"],
    schema: LineShowRequestSchema,
  }),
  defineCommand({
    aliases: {
      b: "b",
      body: "b",
      f: "f",
      proof: "f",
      p: "p",
      position: "p",
      s: "s",
      "session-key": "s",
      sk: "s",
      t: "t",
      target: "t",
      v: "v",
      verdict: "v",
    },
    arrayOrder: ["s", "p", "t", "v", "b", "f"],
    description: "Record a proof and update threat debt",
    fromArray: ([, , s, p, t, v, b, f]) => ({
      b: normalizeOptionalValue(b),
      cmd: "proof.record",
      f: normalizeOptionalValue(f),
      p: normalizeOptionalValue(p),
      s,
      t,
      v,
    }),
    fromFlags: (options, operands) => ({
      b: readValue(options, "b") ?? operands[0],
      cmd: "proof.record",
      f: readValue(options, "f"),
      p: readValue(options, "p"),
      s: readValue(options, "s"),
      t: readValue(options, "t"),
      v: readValue(options, "v"),
    }),
    path: ["proof", "record"],
    schema: ProofRecordRequestSchema,
  }),
  defineCommand({
    aliases: { p: "p", position: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p"],
    description: "List proof atoms from a position snapshot",
    fromArray: ([, , s, p]) => ({ cmd: "proof.list", p: normalizeOptionalValue(p), s }),
    fromFlags: (options) => ({ cmd: "proof.list", p: readValue(options, "p"), s: readValue(options, "s") }),
    path: ["proof", "list"],
    schema: ProofListRequestSchema,
  }),
  defineCommand({
    aliases: { d: "d", depth: "d", p: "p", position: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p", "d"],
    description: "Search the next recommended response set",
    fromArray: ([, , s, p, d]) => ({ cmd: "search.next", d, p: normalizeOptionalValue(p), s }),
    fromFlags: (options) => ({ cmd: "search.next", d: readValue(options, "d"), p: readValue(options, "p"), s: readValue(options, "s") }),
    path: ["search", "next"],
    schema: SearchNextRequestSchema,
  }),
  defineCommand({
    aliases: { d: "d", depth: "d", p: "p", position: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p", "d"],
    description: "Show the search report without mutating the board",
    fromArray: ([, , s, p, d]) => ({ cmd: "search.show", d, p: normalizeOptionalValue(p), s }),
    fromFlags: (options) => ({ cmd: "search.show", d: readValue(options, "d"), p: readValue(options, "p"), s: readValue(options, "s") }),
    path: ["search", "show"],
    schema: SearchShowRequestSchema,
  }),
  defineCommand({
    aliases: { m: "m", mode: "m", p: "p", position: "p", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "p", "m"],
    description: "Render the compact attention report",
    fromArray: ([, s, p, m]) => ({ cmd: "compact", m: normalizeOptionalValue(m), p: normalizeOptionalValue(p), s }),
    fromFlags: (options, operands) => ({
      cmd: "compact",
      m: readValue(options, "m"),
      p: readValue(options, "p") ?? operands[0],
      s: readValue(options, "s"),
    }),
    path: ["compact"],
    schema: CompactRequestSchema,
  }),
  defineCommand({
    aliases: { k: "k", key: "k", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "k"],
    description: "Get one dict entry",
    fromArray: ([, , s, k]) => ({ cmd: "dict.get", k, s }),
    fromFlags: (options) => ({ cmd: "dict.get", k: readValue(options, "k"), s: readValue(options, "s") }),
    path: ["dict", "get"],
    schema: DictGetRequestSchema,
  }),
  defineCommand({
    aliases: { s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s"],
    description: "Get all dict entries",
    fromArray: ([, , s]) => ({ cmd: "dict.getAll", s }),
    fromFlags: (options) => ({ cmd: "dict.getAll", s: readValue(options, "s") }),
    path: ["dict", "get-all"],
    schema: DictGetAllRequestSchema,
  }),
  defineCommand({
    aliases: { k: "k", key: "k", s: "s", "session-key": "s", sk: "s", v: "v", value: "v" },
    arrayOrder: ["s", "k", "v"],
    description: "Set one dict entry",
    fromArray: ([, , s, k, v]) => ({ cmd: "dict.set", k, s, v }),
    fromFlags: (options) => ({ cmd: "dict.set", k: readValue(options, "k"), s: readValue(options, "s"), v: readValue(options, "v") }),
    path: ["dict", "set"],
    schema: DictSetRequestSchema,
  }),
  defineCommand({
    aliases: { k: "k", key: "k", s: "s", "session-key": "s", sk: "s" },
    arrayOrder: ["s", "k"],
    description: "Remove one dict entry",
    fromArray: ([, , s, k]) => ({ cmd: "dict.unset", k, s }),
    fromFlags: (options) => ({ cmd: "dict.unset", k: readValue(options, "k"), s: readValue(options, "s") }),
    path: ["dict", "unset"],
    schema: DictUnsetRequestSchema,
  }),
] as const;

const COMMAND_BY_KEY = new Map(
  COMMAND_DEFINITIONS.map((definition) => [definition.path.join(" "), definition]),
);

export function parseCliArgs(argv: string[]): ParsedCliArgs {
  const raw = parseRawCliArgs(argv);
  const firstPositional = raw.positionals[0]?.trim();
  const machineInput =
    firstPositional?.startsWith("{") === true ||
    firstPositional?.startsWith("[") === true;
  const json = readBooleanValue(raw.options, "json") || machineInput;
  const pretty = readBooleanValue(raw.options, "pretty");
  const version = readBooleanValue(raw.options, "version");
  const help = readBooleanValue(raw.options, "help");
  const cwd = readValue(raw.options, "cwd");

  if (version) {
    return { cwd, help, json, pretty, request: undefined, version: true };
  }

  if (help || raw.positionals.length === 0) {
    return { cwd, help: true, json, pretty, request: undefined, version: false };
  }

  const request = parseRequest(raw.positionals, raw.options);
  return { cwd, help: false, json, pretty, request, version: false };
}

export function describeCliProtocol() {
  return COMMAND_DEFINITIONS.map((definition) => ({
    aliases: definition.aliases,
    arrayOrder: definition.arrayOrder,
    description: definition.description,
    path: definition.path,
  }));
}

function parseRequest(
  positionals: string[],
  rawOptions: Record<string, OptionValue>,
): CliRequest {
  const first = positionals[0];
  if (first === undefined) {
    throw new CliError("INVALID_COMMAND", "Missing command");
  }

  if (first.trim().startsWith("{")) {
    return parseJsonInput(JSON.parse(first));
  }

  if (first.trim().startsWith("[")) {
    return parseJsonInput(JSON.parse(first));
  }

  const definition = findDefinitionForPath(positionals);
  if (definition === undefined) {
    throw new CliError("INVALID_COMMAND", `Unknown command "${positionals.join(" ")}"`);
  }

  const operands = positionals.slice(definition.path.length);
  const candidate = definition.fromFlags(
    normalizeCommandOptions(rawOptions, definition.aliases),
    operands,
  );
  return definition.schema.parse(candidate);
}

function parseJsonInput(value: unknown): CliRequest {
  if (isCommandArray(value)) {
    return parseJsonCommandArray(value);
  }

  if (Array.isArray(value)) {
    return z.array(z.unknown()).min(1).parse(value).map((entry) => parseJsonCommand(entry));
  }

  if (isRecord(value) && Array.isArray(value.batch)) {
    return z.array(z.unknown()).min(1).parse(value.batch).map((entry) => parseJsonCommand(entry));
  }

  return parseJsonCommand(value);
}

function parseJsonCommand(value: unknown): CommandRequest {
  if (isCommandArray(value)) {
    return parseJsonCommandArray(value);
  }
  return CommandRequestSchema.parse(value);
}

function parseJsonCommandArray(value: readonly unknown[]) {
  const pathKey = resolveArrayPath(value);
  const definition = COMMAND_BY_KEY.get(pathKey);
  if (definition === undefined) {
    throw new CliError("INVALID_COMMAND", `Unknown JSON-array command "${pathKey}"`);
  }
  return definition.schema.parse(definition.fromArray(value));
}

function findDefinitionForPath(positionals: string[]) {
  for (const definition of COMMAND_DEFINITIONS) {
    const candidate = positionals.slice(0, definition.path.length);
    if (candidate.join(" ") === definition.path.join(" ")) {
      return definition;
    }
  }
  return undefined;
}

function resolveArrayPath(values: readonly unknown[]) {
  const first = values[0];
  const second = values[1];
  if (typeof first !== "string") {
    throw new CliError("INVALID_COMMAND", "JSON-array command must start with strings");
  }
  if (first === "compact") {
    return "compact";
  }
  if (typeof second !== "string") {
    throw new CliError("INVALID_COMMAND", "JSON-array command must include group and command");
  }
  return `${first} ${second}`;
}

function isCommandArray(value: unknown): value is readonly unknown[] {
  return Array.isArray(value) && typeof value[0] === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function defineCommand(definition: CommandDefinition) {
  return definition;
}

function parseRawCliArgs(argv: string[]): RawCliArgs {
  const options: Record<string, OptionValue> = {};
  const positionals: string[] = [];

  for (let index = 0; index < argv.length; index += 1) {
    const token = argv[index];
    if (token === undefined) {
      break;
    }

    if (!token.startsWith("-") || token === "-") {
      positionals.push(token);
      continue;
    }

    const trimmed = token.startsWith("--") ? token.slice(2) : token.slice(1);
    const separator = trimmed.indexOf("=");
    if (separator >= 0) {
      assignOption(options, trimmed.slice(0, separator), trimmed.slice(separator + 1));
      continue;
    }

    const next = argv[index + 1];
    if (next !== undefined && !next.startsWith("-")) {
      assignOption(options, trimmed, next);
      index += 1;
      continue;
    }

    assignOption(options, trimmed, true);
  }

  return { options, positionals };
}

function normalizeCommandOptions(
  options: Record<string, OptionValue>,
  aliases: Record<string, string>,
) {
  const normalized: Record<string, OptionValue> = {};
  for (const [key, value] of Object.entries(options)) {
    const mapped = aliases[key];
    if (mapped === undefined) {
      continue;
    }
    assignOption(normalized, mapped, value);
  }
  return normalized;
}

function assignOption(
  options: Record<string, OptionValue>,
  key: string,
  value: boolean | string | string[],
) {
  const current = options[key];
  if (current === undefined) {
    options[key] = value;
    return;
  }

  if (Array.isArray(current)) {
    if (Array.isArray(value)) {
      current.push(...value);
      return;
    }
    current.push(String(value));
    return;
  }

  const next = [String(current)];
  if (Array.isArray(value)) {
    next.push(...value);
  } else {
    next.push(String(value));
  }
  options[key] = next;
}

function readValue(options: Record<string, OptionValue>, key: string) {
  const value = options[key];
  if (value === undefined) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.at(-1);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return value;
}

function readValues(options: Record<string, OptionValue>, key: string) {
  const value = options[key];
  if (value === undefined) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.map(String);
  }
  if (typeof value === "boolean") {
    return undefined;
  }
  return [value];
}

function readBooleanValue(options: Record<string, OptionValue>, key: string) {
  const value = options[key];
  if (value === undefined) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.at(-1) === "true";
  }
  if (typeof value === "boolean") {
    return value;
  }
  return value === "true";
}

function normalizeOptionalValue(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  return value;
}
